import { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { ROUTES } from '../constants/routes';
import { ProtectedRoute } from '../wrappers';
import { MainLayout } from '../components';

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
  </div>
);

// Wrapper pour les routes protégées avec MainLayout
const ProtectedLayoutRoute = ({ requiredRole, children }) => (
  <ProtectedRoute requiredRole={requiredRole}>
    <MainLayout>
      <Suspense fallback={<PageLoader />}>
        {children}
      </Suspense>
    </MainLayout>
  </ProtectedRoute>
);

// Wrappers spécifiques par rôle
const AdminRoute = ({ children }) => (
  <ProtectedLayoutRoute requiredRole="admin">
    {children}
  </ProtectedLayoutRoute>
);

const GerantRoute = ({ children }) => (
  <ProtectedLayoutRoute requiredRole="gerant">
    {children}
  </ProtectedLayoutRoute>
);

const ParticipantRoute = ({ children }) => (
  <ProtectedLayoutRoute requiredRole="participant">
    {children}
  </ProtectedLayoutRoute>
);

// Pages Admin - Lazy loaded
const AdminDashboardPage = lazy(() => import('../pages/admin/AdminDashboardPage'));
const AdminUsersPage = lazy(() => import('../pages/admin/AdminUsersPage'));
const AdminSurveysPage = lazy(() => import('../pages/admin/AdminSurveysPage'));
const JournalisationPage = lazy(() => import('../pages/admin/JournalisationPage'));

// Pages Gérant - Lazy loaded
const GerantDashboardPage = lazy(() => import('../pages/gerant/GerantDashboardPage'));
const GerantSurveysPage = lazy(() => import('../pages/gerant/GerantSurveysPage'));
const CreateSurveyPage = lazy(() => import('../pages/gerant/CreateSurveyPage'));
const SurveyDetailPage = lazy(() => import('../pages/gerant/SurveyDetailPage'));
const SurveyResultsPage = lazy(() => import('../pages/gerant/SurveyResultsPage'));
const SurveysModelesPage = lazy(() => import('../pages/gerant/SurveysModelesPage'));
const SurveyParticipantsPage = lazy(() => import('../pages/gerant/SurveyParticipantsPage'));

// Pages admin - Lazy loaded
const Participant = lazy(() => import('../pages/participant/participant'));
const ParticipantHistory = lazy(() => import('../pages/participant/ParticipantHistory'));
const UserProfile = lazy(() => import('../pages/auth/UserProfile'));

/**
 * Routes protégées 
 * Utilise les constantes ROUTES pour les chemins
 */
export const protectedRoutes = [
  // ============ ROUTES ADMIN (/admin) ============
  {
    path: ROUTES.ADMIN.ROOT,
    element: <AdminRoute><AdminDashboardPage /></AdminRoute>,
  },
  {
    path: 'admin/gestion-sondage',
    element: <AdminRoute><AdminSurveysPage /></AdminRoute>,
  },
  { path: 'admin/gestion-sondage/:id', element: <AdminRoute><SurveyDetailPage /></AdminRoute> },
  { path: 'admin/gestion-sondage/:id/resultats', element: <AdminRoute><SurveyResultsPage /></AdminRoute> },
  { path: 'admin/gestion-sondage/:id/participants', element: <AdminRoute><SurveyParticipantsPage /></AdminRoute> },
  {
    path: 'admin/utilisateurs',
    element: <AdminRoute><AdminUsersPage /></AdminRoute>,
  },
  {
    path: 'admin/journalisation',
    element: <AdminRoute><JournalisationPage /></AdminRoute>,
  },
  
  // ============ ROUTES GÉRANT (/gerant) ============
  {
    path: ROUTES.GERANT.ROOT,
    element: <GerantRoute><Navigate to={ROUTES.GERANT.DASHBOARD} replace /></GerantRoute>,
  },
  {
    path: 'gerant/dashboard',
    element: <GerantRoute><GerantDashboardPage /></GerantRoute>,
  },
  {
    path: 'gerant/gestion-sondages',
    element: <GerantRoute><GerantSurveysPage /></GerantRoute>,
    children: [
      { path: '', element: <GerantSurveysPage /> },
      { path: 'nouveau', element: <CreateSurveyPage /> },
      { path: ':id', element: <SurveyDetailPage /> },
      { path: ':id/resultats', element: <SurveyResultsPage /> },
      { path: ':id/participants', element: <SurveyParticipantsPage /> },
      { path: 'modifier/:id', element: <CreateSurveyPage /> },
    ],
  },
  {
    path: 'gerant/modeles-sondages',
    element: <GerantRoute><SurveysModelesPage /></GerantRoute>,
    children: [
      { path: '', element: <SurveysModelesPage /> },
      { path: 'nouveau', element: <CreateSurveyPage /> },
      { path: ':id', element: <SurveyDetailPage /> },
      { path: ':id/resultats', element: <SurveyResultsPage /> },
      { path: ':id/participants', element: <SurveyParticipantsPage /> },
      { path: 'modifier/:id', element: <CreateSurveyPage /> },
    ],
  },

  // ============ ROUTES PARTICIPANT (/participant) ============
  {
    path: ROUTES.PARTICIPANT.ROOT,
    element: <ParticipantRoute><Participant /></ParticipantRoute>,
  },
  {
    path: 'participant/surveys',
    element: <ParticipantRoute><Participant /></ParticipantRoute>,
  },
  {
    path: 'participant/survey/:id/respond',
    element: <ParticipantRoute><Participant /></ParticipantRoute>,
  },
  {
    path: 'participant/history',
    element: <ParticipantRoute><ParticipantHistory /></ParticipantRoute>,
  },

  // ============ ROUTES PROFIL ============
  {
    path: '/profil',
    element: (
      <ProtectedRoute>
        <MainLayout>
          <Suspense fallback={<PageLoader />}>
            <UserProfile />
          </Suspense>
        </MainLayout>
      </ProtectedRoute>
    ),
  },
];